'use client';

import { useEffect, useState } from 'react';
import Image from 'next/image';
import { signIn, useSession } from 'next-auth/react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';

import SfLogoMain from '@/assets/icons/sidebar-nav/sportsfi-logo-main';
import landingPageBg from '@/assets/images/backgrounds/app-log-in-bkg.jpg';

export default function LoginContent(): React.ReactNode {
  const searchParams = useSearchParams();
  const callbackUrl = searchParams.get('callbackUrl');
  const shouldAutoSignIn = searchParams.get('autoSignIn') !== 'false';
  const { status } = useSession();
  const router = useRouter();
  const [authError, setAuthError] = useState<string | null>(null);
  const t = useTranslations('auth');

  useEffect(() => {
    if (status === 'authenticated' && shouldAutoSignIn) {
      router.replace(callbackUrl || '/');
      return;
    }

    if (status === 'unauthenticated' && shouldAutoSignIn) {
      signIn('keycloak', {
        callbackUrl: callbackUrl || '/',
        redirect: true,
      });
    }
  }, [status, router, callbackUrl, shouldAutoSignIn]);

  const handleManualSignIn = () => {
    setAuthError(null);
    signIn('keycloak', { callbackUrl: callbackUrl || '/', redirect: true }, { prompt: 'login', max_age: '0' });
  };

  if (status === 'loading') {
    return (
      <div className="relative z-10 flex min-h-screen flex-col items-center justify-center px-4 text-center">
        <p className="text-lg text-black">{t('loading')}</p>
      </div>
    );
  }

  return (
    <>
      <Image src={landingPageBg} alt="SportsFi Background" fill priority className="object-cover brightness-50" />

      <div className="relative z-10 flex min-h-screen flex-col items-center justify-center px-4 text-center">
        <div className="mb-8">
          <SfLogoMain />
        </div>

        {authError || !shouldAutoSignIn ? (
          <div className="rounded-md bg-red-100 p-6 shadow-lg">
            <h3 className="text-xl font-semibold text-red-800">{t('signInError')}</h3>
            <p className="mt-2 text-red-700">
              {authError
                ? t('couldNotSignIn', { error: authError })
                : 'Your session is no longer valid. Sign in again to continue.'}
            </p>
            {authError && <p className="mt-2 text-sm text-red-600">{t('enablePopups')}</p>}

            <button
              type="button"
              onClick={handleManualSignIn}
              className="mt-6 rounded-md bg-blue-600 px-6 py-3 text-lg font-semibold text-white shadow-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition-colors duration-150"
            >
              {t('trySignInAgain')}
            </button>
          </div>
        ) : (
          <p className="mb-8 max-w-lg text-lg text-white">{t('redirectingToLogin')}</p>
        )}
      </div>
    </>
  );
}
